import React, { createContext, useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

const PaginationContext = createContext(null);

export const usePaginationContext = () => {
	const context = useContext(PaginationContext);
	if (!context) {
		throw new Error(
			'usePaginationContext must be used within PaginationProvider'
		);
	}
	return context;
};

export const PaginationProvider = ({
	children,
	totalCount,
	itemsPerPage,
	offset,
	onPageChange
}) => {
	const goToNextPage = useCallback(() => {
		onPageChange((prev) => Math.min(prev + itemsPerPage, totalCount));
	}, [itemsPerPage, totalCount]);

	const goToPreviousPage = useCallback(() => {
		onPageChange((prev) => Math.max(0, prev - itemsPerPage));
	}, [itemsPerPage]);

	const canGoNext = offset + itemsPerPage < totalCount;
	const canGoPrevious = offset - itemsPerPage >= 0;

	const value = useMemo(
		() => ({
			offset,
			totalCount,
			itemsPerPage,
			canGoNext,
			canGoPrevious,
			goToNextPage,
			goToPreviousPage
		}),
		[
			offset,
			totalCount,
			itemsPerPage,
			canGoNext,
			canGoPrevious,
			goToNextPage,
			goToPreviousPage
		]
	);
	return (
		<PaginationContext.Provider value={value}>
			{children}
		</PaginationContext.Provider>
	);
};

PaginationProvider.propTypes = {
	children: PropTypes.node.isRequired,
	totalCount: PropTypes.number.isRequired,
	onPageChange: PropTypes.func.isRequired,
	itemsPerPage: PropTypes.number.isRequired,
	offset: PropTypes.number.isRequired
};
